import { useLocale } from "../../hooks/useLocale";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Panel from "@ui/panel/Panel";
import type { OverviewTechnicalError } from "../../modules/overviewAnalysis/types";
import { useTranslation } from "react-i18next";
import { getAppLanguage } from "src/i18n";

const date = (value: string) => value.split("-").reverse().join(".");

export const TechnicalErrorsPopup = ({ errors, from, to, currency, onClose }: {
	errors: OverviewTechnicalError[]; from: string; to: string; currency: string; onClose: () => void;
}) => {
	useLocale();
	const { t } = useTranslation();
	const number = (value: number) => value.toLocaleString(getAppLanguage() === "ru" ? "ru-RU" : "en-GB");
	const categoryName = (category: OverviewTechnicalError) => t(`analysis.technicalCategories.${category.id}`, { defaultValue: category.name });
	const message = (value: string) => value
		.replaceAll("[скрыто]", t("analysis.redactedEmail"))
		.replaceAll("[ссылка]", t("analysis.redactedLink"))
		.replaceAll("[номер]", t("analysis.redactedNumber"));
	const popup = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const previousFocus = document.activeElement;
		popup.current?.focus();
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus(); };
	}, []);
	if (typeof document === "undefined") return null;
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={t("analysis.technicalErrors")} tabIndex={-1}>
		<Panel decorator="absolute" label={t("analysis.technicalErrors")} onClose={onClose} growContent
			sx={{ width: "820px", maxWidth: "94vw", maxHeight: "90dvh", overflow: "hidden" }}>
			<div style={{ overflow: "auto", minHeight: 0, minWidth: 0 }}>
				<p style={{ margin: "0 0 8px", color: "#667085", fontSize: 13 }}>{date(from)} — {date(to)} · {currency}</p>
				<p style={{ margin: "0 0 16px", fontSize: 14 }}>{t("analysis.totalMessages", { count: number(errors.reduce((sum, item) => sum + item.count, 0)) })}</p>
				{errors.map(category => <section key={category.id} style={{ border: "1px solid #e1e6ed", borderRadius: 10, marginBottom: 12, padding: 14 }}>
					<div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, marginBottom: 10 }}>
						<strong style={{ fontSize: 14, overflowWrap: "anywhere" }}>{categoryName(category)}</strong>
						<strong style={{ fontSize: 14, whiteSpace: "nowrap", color: "#8a5700", fontVariantNumeric: "tabular-nums" }}>{number(category.count)}</strong>
					</div>
					<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 13 }}>
						<thead><tr>
							<th style={{ textAlign: "left", color: "#667085", fontWeight: 400, paddingBottom: 6 }}>{t("analysis.statisticsMessage")}</th>
							<th style={{ width: 88, textAlign: "right", color: "#667085", fontWeight: 400, paddingBottom: 6 }}>{t("analysis.count")}</th>
						</tr></thead>
						<tbody>{category.errors.map((error, index) => <tr key={index}>
							<td style={{ borderTop: "1px solid #edf0f4", padding: "8px 12px 8px 0", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{message(error.label)}</td>
							<td style={{ borderTop: "1px solid #edf0f4", textAlign: "right", verticalAlign: "top", paddingTop: 8, fontVariantNumeric: "tabular-nums" }}>{number(error.count)}</td>
						</tr>)}</tbody>
					</table>
				</section>)}
				<p style={{ margin: 0, color: "#667085", fontSize: 12, lineHeight: 1.5 }}>{t("analysis.technicalErrorsHint")}</p>
			</div>
		</Panel>
	</div>, document.body);
};
